import { formatDay } from '../lib/time';
import { daysBetween } from './places';
import { ADAR, AV, IYAR, KISLEV, NISAN, SHVAT, SIVAN, TISHREI, fixedToISO, formatHebrewDate, hebrewDateOf, isoToFixed, nextHebrewAnniversary, nextHebrewDate, type HebrewDate } from './hebrewCalendar';
import type { Lang, Person, Relationship, UUID } from './types';

/**
 * "Coming up": the family's recurring days, worked out from what the tree
 * already knows — birthdays from exact birth dates, anniversaries from
 * marriages with a known start, yahrzeits from exact death dates (on the
 * Hebrew calendar), and the chagim. Nothing is stored: it is all derived, so
 * there is nothing to keep in sync and nothing to mark as read.
 */

export type OccasionKind = 'birthday' | 'anniversary' | 'yahrzeit' | 'holiday';

export type HolidayKey = 'roshHashana' | 'yomKippur' | 'sukkot' | 'simchatTorah' | 'chanukah' | 'tuBishvat' | 'purim' | 'pesach' | 'yomHaatzmaut' | 'lagBaomer' | 'shavuot' | 'tishaBav';

export interface Occasion {
  /** Stable within a day: kind + people + date. */
  id: string;
  kind: OccasionKind;
  /** YYYY-MM-DD (civil) when it falls next. */
  date: string;
  /** Days from today: 0 = today. */
  inDays: number;
  /** The people it belongs to (the couple for an anniversary; nobody for a holiday). */
  personIds: UUID[];
  /** Age turning / years married / years since the death — when the original year is known. */
  years?: number;
  /** The Hebrew date (yahrzeits and holidays). */
  hebrew?: HebrewDate;
  holiday?: HolidayKey;
}

/** Fixed Hebrew dates of the holidays shown in "Coming up" (first day of each). */
export const HOLIDAYS: ReadonlyArray<{ key: HolidayKey; month: number; day: number }> = [
  { key: 'roshHashana', month: TISHREI, day: 1 },
  { key: 'yomKippur', month: TISHREI, day: 10 },
  { key: 'sukkot', month: TISHREI, day: 15 },
  { key: 'simchatTorah', month: TISHREI, day: 22 },
  { key: 'chanukah', month: KISLEV, day: 25 },
  { key: 'tuBishvat', month: SHVAT, day: 15 },
  { key: 'purim', month: ADAR, day: 14 },
  { key: 'pesach', month: NISAN, day: 15 },
  { key: 'yomHaatzmaut', month: IYAR, day: 5 },
  { key: 'lagBaomer', month: IYAR, day: 18 },
  { key: 'shavuot', month: SIVAN, day: 6 },
  { key: 'tishaBav', month: AV, day: 9 },
];

/** Exact YYYY-MM-DD of a fuzzy date, or null when it is not known to the day. */
function exactDay(d: Person['birth'] | undefined): string | null {
  if (!d || d.kind !== 'exact' || !d.start || d.start.length < 10) return null;
  return d.start.slice(0, 10);
}

/** Next civil anniversary (month + day) on or after `today`; 29 Feb is kept on 28 Feb in common years. */
export function nextCivilAnniversary(origISO: string, today: string): { iso: string; years: number } {
  const [oy, om, od] = origISO.split('-').map(Number);
  const [ty] = today.split('-').map(Number);
  const candidate = (y: number) => {
    const day = om === 2 && od === 29 && !((y % 4 === 0 && y % 100 !== 0) || y % 400 === 0) ? 28 : od;
    return `${y}-${String(om).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };
  let iso = candidate(ty);
  let year = ty;
  if (iso < today) {
    year = ty + 1;
    iso = candidate(year);
  }
  return { iso, years: year - oy };
}

export interface OccasionSource {
  people: Pick<Person, 'id' | 'status' | 'birth' | 'death' | 'archived' | 'mergedIntoId'>[];
  relationships: Pick<Relationship, 'type' | 'fromPersonId' | 'toPersonId' | 'subtype' | 'current' | 'startDate' | 'status'>[];
}

/**
 * Every occasion falling within `daysAhead` days of `today` (inclusive of
 * today), soonest first. Holidays are included when `withHolidays` is true.
 */
export function upcomingOccasions(src: OccasionSource, today: string, daysAhead = 30, withHolidays = true): Occasion[] {
  const out: Occasion[] = [];
  const alive = new Map<UUID, boolean>();
  for (const p of src.people) alive.set(p.id, p.status !== 'deceased');
  const within = (iso: string) => {
    const d = daysBetween(today, iso);
    return d >= 0 && d <= daysAhead ? d : null;
  };

  for (const p of src.people) {
    if (p.archived || p.mergedIntoId) continue;
    const birth = exactDay(p.birth);
    if (birth && p.status !== 'deceased') {
      const n = nextCivilAnniversary(birth, today);
      const inDays = within(n.iso);
      // A newborn (or a birth date entered in the future) has no age to announce.
      if (inDays !== null) out.push({ id: `birthday:${p.id}:${n.iso}`, kind: 'birthday', date: n.iso, inDays, personIds: [p.id], years: n.years > 0 ? n.years : undefined });
    }
    const death = exactDay(p.death);
    if (death && p.status === 'deceased') {
      const n = nextHebrewAnniversary(death, today);
      const inDays = within(n.iso);
      if (inDays !== null) out.push({ id: `yahrzeit:${p.id}:${n.iso}`, kind: 'yahrzeit', date: n.iso, inDays, personIds: [p.id], years: n.years, hebrew: n.hebrew });
    }
  }

  for (const r of src.relationships) {
    if (r.type !== 'partner' || r.status !== 'approved' || r.current === false || r.subtype !== 'married') continue;
    const start = exactDay(r.startDate);
    if (!start) continue;
    if (!alive.get(r.fromPersonId) || !alive.get(r.toPersonId)) continue;
    const n = nextCivilAnniversary(start, today);
    const inDays = within(n.iso);
    if (inDays !== null) out.push({ id: `anniversary:${r.fromPersonId}:${r.toPersonId}:${n.iso}`, kind: 'anniversary', date: n.iso, inDays, personIds: [r.fromPersonId, r.toPersonId], years: n.years > 0 ? n.years : undefined });
  }

  if (withHolidays) {
    for (const h of HOLIDAYS) {
      // Two observances move around Shabbat; check this year's and, if that has passed, next year's.
      let n = nextHebrewDate(h.month, h.day, today);
      let iso = observedDate(h.key, n.iso);
      if (iso < today) {
        n = nextHebrewDate(h.month, h.day, addDaysISO(n.iso, 1));
        iso = observedDate(h.key, n.iso);
      }
      const inDays = within(iso);
      if (inDays !== null) out.push({ id: `holiday:${h.key}:${iso}`, kind: 'holiday', date: iso, inDays, personIds: [], holiday: h.key, hebrew: hebrewDateOf(iso) });
    }
  }

  return out.sort((a, b) => a.inDays - b.inDays || rank(a.kind) - rank(b.kind));
}

/** 0 = Sunday … 6 = Saturday for a civil date (fixed day 1 was a Monday). */
function weekdayOf(iso: string): number {
  return ((isoToFixed(iso) % 7) + 7) % 7;
}

function addDaysISO(iso: string, days: number): string {
  return fixedToISO(isoToFixed(iso) + days);
}

/**
 * The day a holiday is actually kept. Yom HaAtzmaut (5 Iyar) is brought
 * forward to Thursday when it falls on Friday or Shabbat and pushed to
 * Tuesday when it falls on Monday; Tisha B'Av on Shabbat is kept on Sunday.
 */
export function observedDate(key: HolidayKey, nominalISO: string): string {
  const wd = weekdayOf(nominalISO);
  if (key === 'yomHaatzmaut') {
    if (wd === 5) return addDaysISO(nominalISO, -1);
    if (wd === 6) return addDaysISO(nominalISO, -2);
    if (wd === 1) return addDaysISO(nominalISO, 1);
  }
  if (key === 'tishaBav' && wd === 6) return addDaysISO(nominalISO, 1);
  return nominalISO;
}

function rank(k: OccasionKind): number {
  return k === 'holiday' ? 0 : k === 'birthday' ? 1 : k === 'anniversary' ? 2 : 3;
}

/** The personal occasion (not a holiday) each person has *today*, for the mark on their circle. */
export function occasionsToday(occasions: Occasion[]): Map<UUID, Occasion> {
  const m = new Map<UUID, Occasion>();
  for (const o of occasions) {
    if (o.inDays !== 0 || o.kind === 'holiday') continue;
    for (const id of o.personIds) if (!m.has(id) || rank(o.kind) < rank(m.get(id)!.kind)) m.set(id, o);
  }
  return m;
}

/** The soonest personal occasion for one person, if any is in the list. */
export function nextOccasionFor(occasions: Occasion[], personId: UUID): Occasion | undefined {
  return occasions.find((o) => o.personIds.includes(personId));
}

/** A deceased person's next yahrzeit whatever the horizon (for their profile). */
export function nextYahrzeit(person: Pick<Person, 'status' | 'death'>, today: string): { iso: string; hebrew: HebrewDate; years: number; inDays: number } | null {
  const death = exactDay(person.death);
  if (!death || person.status !== 'deceased') return null;
  const n = nextHebrewAnniversary(death, today);
  return { ...n, inDays: daysBetween(today, n.iso) };
}

/** "today" / "tomorrow" / "Thu 24 Sep" — the short relative day used in Coming up lines. */
export function occasionDayLabel(o: Pick<Occasion, 'date' | 'inDays'>, lang: Lang, labels: { today: string; tomorrow: string }): string {
  if (o.inDays === 0) return labels.today;
  if (o.inDays === 1) return labels.tomorrow;
  // One formatter for all of them (lib/time): "Coming up" writes a line for every day in the next month.
  return formatDay(o.date, lang, { weekday: 'short', day: 'numeric', month: 'short' });
}

export function hebrewLabel(o: Pick<Occasion, 'hebrew'>, lang: Lang): string {
  return o.hebrew ? formatHebrewDate(o.hebrew, lang) : '';
}
